import type { AssetRef } from "@loumy/contracts";
import { useState } from "react";
import { useSession } from "../auth/session";
import { ASSET_DRAG_TYPE } from "../lib/folderTree";
import { FallbackRenderer } from "../results/FallbackRenderer";
import { ResultUrlProvider } from "../results/ResultUrlContext";
import { FolderTree } from "../shell/FolderTree";
import { useAssets } from "../state/queries";
import { useUiStore } from "../state/uiStore";
import { Button, Icon, Spinner } from "../ui";

// §11 Assets-Browser. Ordner-Tree links, Asset-Grid rechts (kind-Fallback-Renderer,
// D32/D43). Inbox = `folder_id IS NULL`-View. D100: ECHTE Vorschauen — GET /v1/assets
// liefert presigned-GET-URLs je Asset (D56/D88), die über den ResultUrl-Context (D65) in
// die Kacheln fließen; ohne URL bleibt die Platzhalter-Kachel (D43, fail-soft).
// D100: Kacheln sind draggable — Drop auf einen Ordner/die Inbox im FolderTree verschiebt
// das Asset (move_asset-RPC; in einen Ordner = kept, §9.13).
// Responsive (§11): ≥md zweispaltig; <md einspaltig, Ordner-Tree über einen Drawer.

export function Assets() {
  const { status } = useSession();
  const { selectedFolderId } = useUiStore();
  const assets = useAssets(selectedFolderId);
  const [drawer, setDrawer] = useState(false);

  const rows = assets.data?.assets ?? [];
  const refs: AssetRef[] = rows.map((a) => ({ id: a.id, kind: a.kind, r2_key: a.r2_key }));
  // Vorschau-URLs (null = keine → Platzhalter); Context erwartet nur vorhandene Strings.
  const urls: Record<string, string> = {};
  for (const [id, url] of Object.entries(assets.data?.downloadUrls ?? {})) {
    if (url) urls[id] = url;
  }

  return (
    // `grid-rows-[minmax(0,1fr)]` fixiert die Zeile auf Viewport-Höhe, damit kein hoher FolderTree die
    // Auto-Zeile aufbläht und das Scrollen der beiden Spalten verschluckt (gleiche Ursache wie Studio.tsx).
    <div className="grid h-full min-h-0 grid-cols-1 grid-rows-[minmax(0,1fr)] md:grid-cols-[260px_minmax(0,1fr)]">
      <aside className="hidden overflow-y-auto border-r border-border bg-surface p-3 md:block">
        <h3 className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted">Ordner</h3>
        <FolderTree />
      </aside>

      <main className="min-h-0 overflow-y-auto p-4">
        {/* Mobile: Ordner-Tree per Drawer erreichbar (auf md+ permanent links) */}
        <div className="mb-3 md:hidden">
          <Button variant="secondary" size="sm" icon="folder" onClick={() => setDrawer(true)}>
            Ordner
          </Button>
        </div>

        {status !== "signed-in" ? (
          <p className="text-sm text-muted">Anmelden, um Assets zu sehen.</p>
        ) : assets.isLoading ? (
          <Spinner />
        ) : (
          <ResultUrlProvider value={urls}>
            <FallbackRenderer
              assets={refs}
              dense
              onTileDragStart={(asset, e) => {
                e.dataTransfer.setData(ASSET_DRAG_TYPE, asset.id);
                e.dataTransfer.effectAllowed = "move";
              }}
            />
          </ResultUrlProvider>
        )}
      </main>

      {drawer && (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawer(false)} />
          <div className="absolute left-0 top-0 h-full w-[280px] max-w-[85vw] overflow-y-auto border-r border-border bg-surface p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-semibold">Ordner</span>
              <button type="button" onClick={() => setDrawer(false)} className="text-muted hover:text-text">
                <Icon name="x" size={18} />
              </button>
            </div>
            <FolderTree />
          </div>
        </div>
      )}
    </div>
  );
}
